import { useEffect, useState } from "react";
import axios from "axios";
import { API, formatApiErrorDetail } from "@/lib/api";
import IntakeForm from "@/components/IntakeForm";
import { Database, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const publicApi = axios.create({ baseURL: API });

export default function PublicIntake() {
  const [meta, setMeta] = useState({ case_types: [], contact_methods: [] });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    publicApi.get("/intake/meta").then((r) => setMeta(r.data)).catch(() => {});
  }, []);

  const onSubmit = async (values) => {
    setSubmitting(true);
    try {
      await publicApi.post("/public/intakes", values);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-5 py-5 flex items-center gap-2.5">
          <div className="w-9 h-9 bg-[#002FA7] flex items-center justify-center rounded-sm">
            <Database className="w-5 h-5 text-white" strokeWidth={2} />
          </div>
          <span className="font-heading font-extrabold text-lg tracking-tight text-gray-900">ClientCases</span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-8">
        {done ? (
          <div className="bg-white border border-gray-200 rounded-sm p-10 text-center shadow-sm" data-testid="intake-thank-you">
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
            <h1 className="font-heading text-2xl font-extrabold text-gray-900 mb-2">Thank you</h1>
            <p className="text-sm text-gray-600 max-w-md mx-auto">
              Your intake has been received. A member of our team will review your information and reach out to you shortly.
            </p>
            <button onClick={() => setDone(false)} data-testid="submit-another-intake"
              className="mt-6 text-sm font-semibold text-[#002FA7] hover:underline">
              Submit another intake
            </button>
          </div>
        ) : (
          <>
            <div className="mb-6">
              <h1 className="font-heading text-3xl font-extrabold tracking-tight text-gray-900">Client Intake Form</h1>
              <p className="text-sm text-gray-500 mt-1">
                Please tell us about yourself and your case. Fields marked * are required.
              </p>
            </div>
            <IntakeForm
              caseTypes={meta.case_types}
              contactMethods={meta.contact_methods}
              onSubmit={onSubmit}
              submitting={submitting}
              submitLabel="Submit intake"
            />
          </>
        )}
        <p className="text-center text-xs text-gray-400 mt-8">© {new Date().getFullYear()} ClientCases</p>
      </main>
    </div>
  );
}
